import { useState, useRef, useEffect, useCallback, useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
    PaperAirplaneIcon,
    PaperClipIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    HandThumbUpIcon,
    CheckIcon,
    EyeIcon,
    PlusIcon,
    StarIcon,
    ArrowDownTrayIcon,
    DocumentTextIcon,
    BoltIcon,
    TicketIcon,
    ComputerDesktopIcon,
    CommandLineIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    InformationCircleIcon,
    ClockIcon,
    ChatBubbleLeftRightIcon,
    UserCircleIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    LinkIcon,
} from "@heroicons/react/20/solid";
import { MOCK_CHAT_MESSAGES, MOCK_AGENT } from "../data/chat";
import type { ChatMessage } from "../data/chat";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ReactionEmoji = "thumbsUp" | "check" | "eyes" | "plus";

interface Reaction {
    emoji: ReactionEmoji;
    count: number;
    reacted: boolean;
}

interface EnrichedMessage extends ChatMessage {
    reactions: Record<ReactionEmoji, Reaction>;
    readAt?: string;
    attachment?: FileAttachment;
    searchHighlight?: boolean;
}

interface FileAttachment {
    name: string;
    size: string;
    type: string;
}

interface RelatedTicket {
    id: string;
    title: string;
    status: "open" | "in-progress" | "closed";
}

interface QuickAction {
    id: string;
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    confirmText: string;
}

interface CannedResponse {
    id: string;
    text: string;
    category: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REACTION_EMOJI_MAP: Record<ReactionEmoji, string> = {
    thumbsUp: "\uD83D\uDC4D",
    check: "\u2705",
    eyes: "\uD83D\uDC40",
    plus: "\u2795",
};

const REACTION_KEYS: ReactionEmoji[] = ["thumbsUp", "check", "eyes", "plus"];

const REACTION_ICONS: Record<ReactionEmoji, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    thumbsUp: HandThumbUpIcon,
    check: CheckIcon,
    eyes: EyeIcon,
    plus: PlusIcon,
};

const QUICK_ACTIONS: QuickAction[] = [
    {
        id: "escalate",
        label: "Escalate to Tier 2",
        icon: BoltIcon,
        confirmText: "Escalation request sent to Tier 2 team",
    },
    { id: "ticket", label: "Create Ticket", icon: TicketIcon, confirmText: "Ticket TKT-006 created successfully" },
    { id: "screen", label: "Share Screen", icon: ComputerDesktopIcon, confirmText: "Screen sharing session initiated" },
    { id: "logs", label: "Send Logs", icon: CommandLineIcon, confirmText: "System logs package uploaded (3.2 MB)" },
];

const CANNED_RESPONSES: CannedResponse[] = [
    { id: "cr1", text: "Can you provide the ticket ID?", category: "Information" },
    { id: "cr2", text: "I'll escalate this to the security team.", category: "Action" },
    { id: "cr3", text: "Please share the affected system details.", category: "Information" },
    { id: "cr4", text: "I've documented this in the incident report.", category: "Action" },
    { id: "cr5", text: "Let me check the correlation data for that event.", category: "Investigation" },
    { id: "cr6", text: "Can you confirm the IP address range affected?", category: "Information" },
];

const RELATED_TICKETS: RelatedTicket[] = [
    { id: "TKT-005", title: "Phishing follow-up training (29 users)", status: "open" },
    { id: "SOC-1250", title: "Suspicious login on ad-dc-01", status: "in-progress" },
    { id: "PHISH-198", title: "Q2 IT Helpdesk simulation click", status: "closed" },
];

const MENTIONED_EVENTS = [
    { id: "SOC-1250", system: "SOC", description: "Suspicious AD login" },
    { id: "PHISH-198", system: "PHISH", description: "Q2 simulation click" },
    { id: "TKT-005", system: "Tickets", description: "Phishing follow-up" },
];

// ---------------------------------------------------------------------------
// Utility: build initial enriched messages
// ---------------------------------------------------------------------------

function enrichMessages(msgs: ChatMessage[]): EnrichedMessage[] {
    return msgs.map((msg) => {
        const base: EnrichedMessage = {
            ...msg,
            reactions: buildEmptyReactions(),
        };
        // Add read receipts to user messages (all past messages are "read")
        if (msg.type === "user") {
            base.readAt = msg.time;
        }
        // Inject a file attachment on the last agent message (m11)
        if (msg.id === "m11") {
            base.attachment = {
                name: "incident_report_SOC-1250.pdf",
                size: "2.4 MB",
                type: "application/pdf",
            };
        }
        return base;
    });
}

function buildEmptyReactions(): Record<ReactionEmoji, Reaction> {
    return {
        thumbsUp: { emoji: "thumbsUp", count: 0, reacted: false },
        check: { emoji: "check", count: 0, reacted: false },
        eyes: { emoji: "eyes", count: 0, reacted: false },
        plus: { emoji: "plus", count: 0, reacted: false },
    };
}

// ---------------------------------------------------------------------------
// Utility: format text with highlighted entities
// ---------------------------------------------------------------------------

function formatMessageText(text: string, highlight?: boolean): React.ReactNode {
    // Patterns to detect: IP addresses, SOC-xxxx / TKT-xxxx / PHISH-xxx IDs, URLs
    const pattern = /(\b(?:SOC|TKT|PHISH|NDR|ZTNA)-\d+\b)|((?:\d{1,3}\.){3}\d{1,3})|(https?:\/\/[^\s]+)/g;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        const matched = match[0];
        if (match[1]) {
            parts.push(
                <span
                    key={match.index}
                    className="inline-block rounded bg-blue-500/10 px-1.5 py-0.5 font-mono text-[13px] font-semibold text-blue-600 dark:bg-blue-400/10 dark:text-blue-400"
                >
                    {matched}
                </span>,
            );
        } else if (match[2]) {
            parts.push(
                <span
                    key={match.index}
                    className="inline-block rounded bg-amber-500/10 px-1.5 py-0.5 font-mono text-[13px] font-medium text-amber-700 dark:bg-amber-400/10 dark:text-amber-400"
                >
                    {matched}
                </span>,
            );
        } else if (match[3]) {
            parts.push(
                <span
                    key={match.index}
                    className="cursor-pointer font-medium text-blue-600 underline decoration-blue-400/40 underline-offset-2 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    {matched}
                </span>,
            );
        }
        lastIndex = match.index + matched.length;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    if (highlight) {
        return (
            <span className="rounded bg-yellow-200/60 dark:bg-yellow-500/20">{parts.length > 0 ? parts : text}</span>
        );
    }

    return parts.length > 0 ? parts : text;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TypingIndicator({ agentName }: { agentName: string }) {
    return (
        <div className="flex justify-start">
            <div className="max-w-[75%] rounded-2xl bg-zinc-100 px-4 py-3 dark:bg-zinc-700/60">
                <p className="mb-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400">{agentName}</p>
                <div className="flex items-center gap-1">
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">{agentName} is typing</span>
                    <span className="flex gap-0.5">
                        <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:0ms]" />
                        <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:150ms]" />
                        <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:300ms]" />
                    </span>
                </div>
            </div>
        </div>
    );
}

function ReactionBar({ onReact }: { onReact: (emoji: ReactionEmoji) => void }) {
    return (
        <div className="flex items-center gap-0.5 rounded-full border border-zinc-200/60 bg-white/90 px-1.5 py-1 shadow-lg backdrop-blur-xl transition-all dark:border-zinc-600/60 dark:bg-zinc-800/90">
            {REACTION_KEYS.map((key) => {
                const Icon = REACTION_ICONS[key];
                return (
                    <button
                        key={key}
                        onClick={(e) => {
                            e.stopPropagation();
                            onReact(key);
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 transition-all hover:scale-125 hover:bg-blue-50 hover:text-blue-500 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
                        title={REACTION_EMOJI_MAP[key]}
                    >
                        <Icon className="h-3.5 w-3.5" />
                    </button>
                );
            })}
        </div>
    );
}

function ReactionPills({ reactions }: { reactions: Record<ReactionEmoji, Reaction> }) {
    const active = REACTION_KEYS.filter((k) => reactions[k].count > 0);
    if (active.length === 0) return null;
    return (
        <div className="mt-1 flex flex-wrap gap-1">
            {active.map((key) => (
                <span
                    key={key}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-all ${
                        reactions[key].reacted
                            ? "border-blue-400/40 bg-blue-50 text-blue-600 dark:border-blue-500/30 dark:bg-blue-900/20 dark:text-blue-400"
                            : "border-zinc-200/60 bg-zinc-50 text-zinc-500 dark:border-zinc-700/40 dark:bg-zinc-800 dark:text-zinc-400"
                    }`}
                >
                    {REACTION_EMOJI_MAP[key]} {reactions[key].count}
                </span>
            ))}
        </div>
    );
}

function FileAttachmentCard({ attachment }: { attachment: FileAttachment }) {
    return (
        <div className="mt-2 flex items-center gap-3 rounded-xl border border-zinc-200/50 bg-white/60 p-3 shadow-sm backdrop-blur-sm transition-all hover:shadow-md dark:border-zinc-600/40 dark:bg-zinc-700/40">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-red-400 to-red-600 shadow-sm">
                <DocumentTextIcon className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-zinc-800 dark:text-zinc-200">{attachment.name}</p>
                <p className="text-xs text-zinc-400">
                    {attachment.size} &middot; {attachment.type.split("/")[1]?.toUpperCase()}
                </p>
            </div>
            <button className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200/40 bg-white/80 text-zinc-500 shadow-sm transition-all hover:border-blue-400/40 hover:text-blue-500 hover:shadow-md dark:border-zinc-600/40 dark:bg-zinc-800/60 dark:hover:text-blue-400">
                <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
        </div>
    );
}

function ReadReceipt({ readAt, isLast }: { readAt?: string; isLast: boolean }) {
    return (
        <span className="inline-flex items-center gap-0.5">
            <CheckIcon className="h-3 w-3 text-blue-300" />
            <CheckIcon className="-ml-1.5 h-3 w-3 text-blue-300" />
            {isLast && readAt && <span className="ml-1 text-[9px] text-blue-200">Read {readAt}</span>}
        </span>
    );
}

function Toast({ message, visible }: { message: string; visible: boolean }) {
    return (
        <div
            className={`fixed bottom-8 left-1/2 z-50 -translate-x-1/2 transform transition-all duration-500 ${
                visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
            }`}
        >
            <div className="flex items-center gap-2 rounded-2xl border border-emerald-500/20 bg-emerald-900/90 px-5 py-3 text-sm font-medium text-emerald-100 shadow-2xl shadow-emerald-500/20 backdrop-blur-xl">
                <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
                {message}
            </div>
        </div>
    );
}

function QuickActionButton({ action, onClick }: { action: QuickAction; onClick: () => void }) {
    const Icon = action.icon;
    return (
        <button
            onClick={onClick}
            className="group flex items-center gap-2 rounded-xl border border-zinc-200/30 bg-white/40 px-3.5 py-2 text-xs font-medium text-zinc-700 shadow-sm backdrop-blur-xl transition-all duration-300 hover:scale-[1.03] hover:border-blue-400/30 hover:bg-white/60 hover:shadow-md hover:shadow-blue-500/10 active:scale-[0.98] dark:border-zinc-700/30 dark:bg-zinc-800/40 dark:text-zinc-300 dark:hover:border-blue-500/30 dark:hover:bg-zinc-800/60"
        >
            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 transition-all group-hover:from-blue-500/20 group-hover:to-purple-500/20">
                <Icon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            {action.label}
        </button>
    );
}

function CannedResponseDropdown({
    responses,
    visible,
    onSelect,
    selectedIndex,
}: {
    responses: CannedResponse[];
    visible: boolean;
    onSelect: (text: string) => void;
    selectedIndex: number;
}) {
    if (!visible) return null;
    return (
        <div className="absolute bottom-full left-0 right-0 mb-2 max-h-64 overflow-y-auto rounded-xl border border-zinc-200/50 bg-white/95 shadow-2xl shadow-blue-500/10 backdrop-blur-xl dark:border-zinc-700/40 dark:bg-zinc-800/95">
            <div className="p-2">
                <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                    Canned Responses
                </p>
                {responses.map((cr, idx) => (
                    <button
                        key={cr.id}
                        onClick={() => onSelect(cr.text)}
                        className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all ${
                            idx === selectedIndex
                                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                : "text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-700/40"
                        }`}
                    >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-zinc-100 dark:bg-zinc-700">
                            <ChatBubbleLeftRightIcon className="h-3 w-3 text-zinc-400" />
                        </div>
                        <div>
                            <p className="font-medium">{cr.text}</p>
                            <p className="mt-0.5 text-[10px] text-zinc-400">{cr.category}</p>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

function ChatEndOverlay({
    visible,
    rating,
    onRate,
    onConfirm,
    onCancel,
}: {
    visible: boolean;
    rating: number;
    onRate: (n: number) => void;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    if (!visible) return null;
    return (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-sm rounded-2xl border border-zinc-200/50 bg-white/95 p-8 shadow-2xl backdrop-blur-xl dark:border-zinc-700/40 dark:bg-zinc-800/95">
                <div className="text-center">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-amber-500" />
                    <h3 className="mt-3 text-lg font-bold text-zinc-900 dark:text-white">End Chat Session?</h3>
                    <p className="mt-2 text-sm text-zinc-500">Please rate your experience before ending.</p>
                    <div className="mt-5 flex justify-center gap-1.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} onClick={() => onRate(n)} className="transition-all hover:scale-110">
                                <StarIcon
                                    className={`h-8 w-8 ${
                                        n <= rating
                                            ? "text-amber-400 drop-shadow-sm"
                                            : "text-zinc-200 dark:text-zinc-600"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                    {rating > 0 && (
                        <p className="mt-2 text-xs text-zinc-400">
                            {rating === 5
                                ? "Excellent!"
                                : rating >= 4
                                  ? "Very good"
                                  : rating >= 3
                                    ? "Good"
                                    : rating >= 2
                                      ? "Fair"
                                      : "Poor"}
                        </p>
                    )}
                    <div className="mt-6 flex gap-3">
                        <button
                            onClick={onCancel}
                            className="flex-1 rounded-xl border border-zinc-200/60 bg-white/80 px-4 py-2.5 text-sm font-medium text-zinc-700 transition-all hover:bg-zinc-50 dark:border-zinc-700/40 dark:bg-zinc-800/60 dark:text-zinc-300 dark:hover:bg-zinc-700/40"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={rating === 0}
                            className="flex-1 rounded-xl bg-gradient-to-r from-red-500 to-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:from-red-600 hover:to-red-700 disabled:opacity-40"
                        >
                            End Chat
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SidebarPanel({
    visible,
    onClose,
    sessionStart,
    sessionDuration,
    messageCount,
}: {
    visible: boolean;
    onClose: () => void;
    sessionStart: string;
    sessionDuration: string;
    messageCount: number;
}) {
    if (!visible) return null;

    const statusColor: Record<string, string> = {
        open: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        "in-progress": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
        closed: "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400",
    };

    return (
        <div className="flex h-full w-80 shrink-0 flex-col border-l border-zinc-200/30 bg-white/50 backdrop-blur-xl dark:border-zinc-700/30 dark:bg-zinc-800/50">
            <div className="flex items-center justify-between border-b border-zinc-200/30 px-4 py-3 dark:border-zinc-700/30">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Session Info</h3>
                <button
                    onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-4">
                {/* Agent profile */}
                <div className="rounded-xl border border-zinc-200/40 bg-white/60 p-4 backdrop-blur-sm dark:border-zinc-700/40 dark:bg-zinc-800/60">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-base font-bold text-white shadow-md shadow-blue-500/20">
                                {MOCK_AGENT.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                            </div>
                            <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500 dark:border-zinc-800" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-zinc-900 dark:text-white">{MOCK_AGENT.name}</p>
                            <p className="text-xs text-zinc-500">{MOCK_AGENT.role}</p>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-700/40">
                            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">Response</p>
                            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-200">~2 min</p>
                        </div>
                        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-700/40">
                            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">
                                Satisfaction
                            </p>
                            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-200">98%</p>
                        </div>
                    </div>
                </div>

                {/* Session info */}
                <div>
                    <h4 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-400">
                        <ClockIcon className="h-3.5 w-3.5" /> Session
                    </h4>
                    <div className="space-y-2.5">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-500">Started</span>
                            <span className="font-mono text-zinc-700 dark:text-zinc-300">{sessionStart}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-500">Duration</span>
                            <span className="font-mono text-zinc-700 dark:text-zinc-300">{sessionDuration}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-500">Messages</span>
                            <span className="font-mono text-zinc-700 dark:text-zinc-300">{messageCount}</span>
                        </div>
                    </div>
                </div>

                {/* Related tickets */}
                <div>
                    <h4 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-400">
                        <TicketIcon className="h-3.5 w-3.5" /> Related Tickets
                    </h4>
                    <div className="space-y-2">
                        {RELATED_TICKETS.map((ticket) => (
                            <div
                                key={ticket.id}
                                className="group flex items-center gap-2 rounded-lg border border-zinc-200/30 bg-white/40 p-2.5 transition-all hover:border-blue-400/30 hover:bg-white/60 dark:border-zinc-700/30 dark:bg-zinc-800/40 dark:hover:border-blue-500/30 dark:hover:bg-zinc-800/60"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-xs font-semibold text-zinc-800 dark:text-zinc-200">
                                        {ticket.id}
                                    </p>
                                    <p className="truncate text-[11px] text-zinc-500">{ticket.title}</p>
                                </div>
                                <span
                                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColor[ticket.status]}`}
                                >
                                    {ticket.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mentioned events */}
                <div>
                    <h4 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-400">
                        <LinkIcon className="h-3.5 w-3.5" /> Mentioned Events
                    </h4>
                    <div className="space-y-1.5">
                        {MENTIONED_EVENTS.map((evt) => (
                            <button
                                key={evt.id}
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-all hover:bg-blue-50/50 dark:hover:bg-blue-900/20"
                            >
                                <span className="rounded bg-blue-500/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-blue-600 dark:bg-blue-400/10 dark:text-blue-400">
                                    {evt.id}
                                </span>
                                <span className="flex-1 truncate text-xs text-zinc-600 dark:text-zinc-400">
                                    {evt.description}
                                </span>
                                <ChevronRightIcon className="h-3 w-3 text-zinc-300 dark:text-zinc-600" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ChatView() {
    const { t } = useTranslation();

    // -- Core chat state --
    const [messages, setMessages] = useState<EnrichedMessage[]>(() => enrichMessages(MOCK_CHAT_MESSAGES));
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // -- Search state --
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    // -- Sidebar state --
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // -- Canned responses state --
    const [cannedOpen, setCannedOpen] = useState(false);
    const [cannedIndex, setCannedIndex] = useState(0);

    // -- Quick action toast state --
    const [toastMessage, setToastMessage] = useState("");
    const [toastVisible, setToastVisible] = useState(false);
    const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // -- Chat end state --
    const [endChatOpen, setEndChatOpen] = useState(false);
    const [chatEnded, setChatEnded] = useState(false);
    const [rating, setRating] = useState(0);

    // -- Session timer --
    const [sessionSeconds, setSessionSeconds] = useState(0);
    const sessionStartTime = useRef("14:02");

    // -- Hover reaction state --
    const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);

    // Session timer effect
    useEffect(() => {
        const interval = setInterval(() => {
            setSessionSeconds((s) => s + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const sessionDuration = useMemo(() => {
        const mins = Math.floor(sessionSeconds / 60);
        const secs = sessionSeconds % 60;
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }, [sessionSeconds]);

    // Auto-scroll
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    // Focus search input when opened
    useEffect(() => {
        if (searchOpen) {
            searchInputRef.current?.focus();
        }
    }, [searchOpen]);

    // Search matches
    const searchMatches = useMemo(() => {
        if (!searchQuery.trim()) return { count: 0, ids: new Set<string>() };
        const q = searchQuery.toLowerCase();
        const ids = new Set<string>();
        messages.forEach((msg) => {
            if (msg.text.toLowerCase().includes(q)) {
                ids.add(msg.id);
            }
        });
        return { count: ids.size, ids };
    }, [searchQuery, messages]);

    // Canned responses visibility
    useEffect(() => {
        if (input === "/") {
            setCannedOpen(true);
            setCannedIndex(0);
        } else if (!input.startsWith("/") || input.length > 1) {
            setCannedOpen(false);
        }
    }, [input]);

    // -- Handlers --

    const showToast = useCallback((message: string) => {
        if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        setToastMessage(message);
        setToastVisible(true);
        toastTimeoutRef.current = setTimeout(() => {
            setToastVisible(false);
        }, 3000);
    }, []);

    const handleSend = useCallback(() => {
        if (!input.trim() || chatEnded) return;
        const now = new Date();
        const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`;
        const userMsg: EnrichedMessage = {
            id: `user-${Date.now()}`,
            type: "user",
            author: "You",
            text: input.trim(),
            time: timeStr,
            reactions: buildEmptyReactions(),
            readAt: timeStr,
        };
        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setIsTyping(true);

        setTimeout(() => {
            setIsTyping(false);
            const replyMsg: EnrichedMessage = {
                id: `agent-${Date.now()}`,
                type: "agent",
                author: MOCK_AGENT.name,
                text: t("chat.autoReply"),
                time: timeStr,
                reactions: buildEmptyReactions(),
            };
            setMessages((prev) => [...prev, replyMsg]);
        }, 1500);
    }, [input, chatEnded, t]);

    const handleReaction = useCallback((msgId: string, emoji: ReactionEmoji) => {
        setMessages((prev) =>
            prev.map((msg) => {
                if (msg.id !== msgId) return msg;
                const current = msg.reactions[emoji];
                return {
                    ...msg,
                    reactions: {
                        ...msg.reactions,
                        [emoji]: {
                            ...current,
                            count: current.reacted ? current.count - 1 : current.count + 1,
                            reacted: !current.reacted,
                        },
                    },
                };
            }),
        );
    }, []);

    const handleCannedSelect = useCallback((text: string) => {
        setInput(text);
        setCannedOpen(false);
        inputRef.current?.focus();
    }, []);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (cannedOpen) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setCannedIndex((i) => Math.min(i + 1, CANNED_RESPONSES.length - 1));
                    return;
                }
                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setCannedIndex((i) => Math.max(i - 1, 0));
                    return;
                }
                if (e.key === "Enter") {
                    e.preventDefault();
                    handleCannedSelect(CANNED_RESPONSES[cannedIndex].text);
                    return;
                }
                if (e.key === "Escape") {
                    setCannedOpen(false);
                    setInput("");
                    return;
                }
            }
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [cannedOpen, cannedIndex, handleCannedSelect, handleSend],
    );

    const handleEndChat = useCallback(() => {
        setChatEnded(true);
        setEndChatOpen(false);
        const now = new Date();
        const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`;
        setMessages((prev) => [
            ...prev,
            {
                id: `sys-end-${Date.now()}`,
                type: "system",
                text: `Chat session ended. Rating: ${"*".repeat(rating)}/5`,
                time: timeStr,
                reactions: buildEmptyReactions(),
            },
        ]);
        showToast("Chat session ended. Thank you for your feedback!");
    }, [rating, showToast]);

    // Find last user message index for "Read" label
    const lastUserMsgIdx = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].type === "user") return i;
        }
        return -1;
    }, [messages]);

    // -- Render --

    return (
        <div className="relative flex h-[calc(100vh-8rem)] overflow-hidden rounded-2xl border border-zinc-200/50 bg-white/70 shadow-lg backdrop-blur-xl dark:border-zinc-700/40 dark:bg-zinc-800/70">
            {/* Main chat area */}
            <div className="flex min-w-0 flex-1 flex-col">
                {/* Header */}
                <div className="relative border-b border-zinc-200/30 dark:border-zinc-700/30">
                    <div className="flex items-center gap-3 px-6 py-4">
                        <div className="relative">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-sm font-bold text-white shadow-md shadow-blue-500/20">
                                {MOCK_AGENT.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                            </div>
                            <div
                                className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${chatEnded ? "bg-zinc-400" : "bg-emerald-500"} dark:border-zinc-800`}
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-zinc-900 dark:text-white">{MOCK_AGENT.name}</p>
                            <p className="text-xs text-zinc-500">
                                {MOCK_AGENT.role} &middot;{" "}
                                <span className={chatEnded ? "text-zinc-400" : "text-emerald-500"}>
                                    {chatEnded ? "Offline" : t("chat.online")}
                                </span>{" "}
                                &middot; Avg. response: 2 min
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Session timer */}
                            <div className="flex items-center gap-1.5 rounded-lg border border-zinc-200/30 bg-zinc-50/60 px-2.5 py-1.5 dark:border-zinc-700/30 dark:bg-zinc-800/40">
                                <ClockIcon className="h-3.5 w-3.5 text-zinc-400" />
                                <span className="font-mono text-xs font-medium text-zinc-600 dark:text-zinc-400">
                                    {sessionDuration}
                                </span>
                            </div>

                            {/* Search toggle */}
                            <button
                                onClick={() => {
                                    setSearchOpen(!searchOpen);
                                    setSearchQuery("");
                                }}
                                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all ${
                                    searchOpen
                                        ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                        : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                                }`}
                            >
                                <MagnifyingGlassIcon className="h-4 w-4" />
                            </button>

                            {/* Sidebar toggle */}
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all ${
                                    sidebarOpen
                                        ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                        : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                                }`}
                            >
                                <InformationCircleIcon className="h-4 w-4" />
                            </button>

                            {/* End Chat */}
                            {!chatEnded && (
                                <button
                                    onClick={() => setEndChatOpen(true)}
                                    className="flex items-center gap-1.5 rounded-lg border border-red-200/40 bg-red-50/60 px-3 py-1.5 text-xs font-medium text-red-600 transition-all hover:border-red-300/50 hover:bg-red-100/60 dark:border-red-800/30 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                                >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                    End Chat
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search bar (expandable) */}
                    {searchOpen && (
                        <div className="flex items-center gap-2 border-t border-zinc-200/20 px-6 py-2.5 dark:border-zinc-700/20">
                            <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-zinc-400" />
                            <input
                                ref={searchInputRef}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search messages..."
                                className="flex-1 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-white"
                            />
                            {searchQuery && (
                                <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                    {searchMatches.count} match{searchMatches.count !== 1 ? "es" : ""}
                                </span>
                            )}
                            <button
                                onClick={() => {
                                    setSearchOpen(false);
                                    setSearchQuery("");
                                }}
                                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Quick Actions Bar */}
                <div className="flex items-center gap-2 overflow-x-auto border-b border-zinc-200/20 px-6 py-2.5 dark:border-zinc-700/20">
                    {QUICK_ACTIONS.map((action) => (
                        <QuickActionButton
                            key={action.id}
                            action={action}
                            onClick={() => showToast(action.confirmText)}
                        />
                    ))}
                </div>

                {/* Messages */}
                <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
                    {messages.map((msg, idx) => {
                        const isHighlighted = searchQuery && searchMatches.ids.has(msg.id);

                        if (msg.type === "system") {
                            return (
                                <div key={msg.id} className="flex justify-center">
                                    <span
                                        className={`rounded-full px-3 py-1 text-xs ${
                                            isHighlighted
                                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300"
                                                : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
                                        }`}
                                    >
                                        {msg.text}
                                    </span>
                                </div>
                            );
                        }

                        const isUser = msg.type === "user";
                        const isLastUserMsg = idx === lastUserMsgIdx;

                        return (
                            <div
                                key={msg.id}
                                className={`group/msg flex ${isUser ? "justify-end" : "justify-start"}`}
                                onMouseEnter={() => setHoveredMsgId(msg.id)}
                                onMouseLeave={() => setHoveredMsgId(null)}
                            >
                                <div className="relative max-w-[75%]">
                                    {/* Reaction bar (appears on hover) */}
                                    <div
                                        className={`absolute ${isUser ? "left-0 -translate-x-full pr-2" : "right-0 translate-x-full pl-2"} top-1/2 -translate-y-1/2 transition-all duration-200 ${
                                            hoveredMsgId === msg.id
                                                ? "scale-100 opacity-100"
                                                : "pointer-events-none scale-95 opacity-0"
                                        }`}
                                    >
                                        <ReactionBar onReact={(emoji) => handleReaction(msg.id, emoji)} />
                                    </div>

                                    {/* Message bubble */}
                                    <div
                                        className={`rounded-2xl px-4 py-2.5 transition-all duration-200 ${
                                            isHighlighted
                                                ? isUser
                                                    ? "bg-blue-600 ring-2 ring-yellow-400 ring-offset-2 dark:ring-offset-zinc-900"
                                                    : "bg-zinc-100 ring-2 ring-yellow-400 ring-offset-2 dark:bg-zinc-700/60 dark:ring-offset-zinc-900"
                                                : isUser
                                                  ? "bg-blue-600 text-white"
                                                  : "bg-zinc-100 text-zinc-900 dark:bg-zinc-700/60 dark:text-zinc-100"
                                        } ${isUser ? "text-white" : "text-zinc-900 dark:text-zinc-100"}`}
                                    >
                                        {!isUser && (
                                            <p className="mb-0.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
                                                {msg.author}
                                            </p>
                                        )}
                                        <p className="text-sm leading-relaxed">
                                            {formatMessageText(msg.text, !!isHighlighted)}
                                        </p>

                                        {/* File attachment */}
                                        {msg.attachment && <FileAttachmentCard attachment={msg.attachment} />}

                                        {/* Time + read receipt */}
                                        <div
                                            className={`mt-1 flex items-center justify-end gap-1.5 text-[10px] ${isUser ? "text-blue-200" : "text-zinc-400"}`}
                                        >
                                            <span>{msg.time}</span>
                                            {isUser && <ReadReceipt readAt={msg.readAt} isLast={isLastUserMsg} />}
                                        </div>
                                    </div>

                                    {/* Reaction pills */}
                                    <ReactionPills reactions={msg.reactions} />
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing indicator */}
                    {isTyping && <TypingIndicator agentName={MOCK_AGENT.name} />}

                    <div ref={bottomRef} />
                </div>

                {/* Input area */}
                <div className="relative border-t border-zinc-200/30 px-4 py-3 dark:border-zinc-700/30">
                    {/* Canned responses dropdown */}
                    <CannedResponseDropdown
                        responses={CANNED_RESPONSES}
                        visible={cannedOpen}
                        onSelect={handleCannedSelect}
                        selectedIndex={cannedIndex}
                    />

                    <div className="flex items-end gap-2">
                        {/* Attachment button */}
                        <button
                            onClick={() => showToast("File picker opened (mock)")}
                            disabled={chatEnded}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-zinc-200/40 bg-white/60 text-zinc-400 shadow-sm transition-all hover:border-blue-400/30 hover:text-blue-500 hover:shadow-md disabled:opacity-40 dark:border-zinc-700/40 dark:bg-zinc-800/60 dark:hover:text-blue-400"
                        >
                            <PaperClipIcon className="h-4 w-4" />
                        </button>

                        <textarea
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={chatEnded ? "Chat has ended" : t("chat.placeholder")}
                            disabled={chatEnded}
                            rows={1}
                            className="flex-1 resize-none rounded-xl border border-zinc-200/40 bg-white/60 px-4 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-blue-400/40 focus:shadow-sm disabled:opacity-40 dark:border-zinc-700/40 dark:bg-zinc-800/60 dark:text-white"
                        />

                        <button
                            onClick={handleSend}
                            disabled={!input.trim() || chatEnded}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm transition-all hover:bg-blue-700 disabled:opacity-40 disabled:hover:bg-blue-600"
                        >
                            <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Hint for canned responses */}
                    {!chatEnded && !input && (
                        <p className="mt-1.5 text-[10px] text-zinc-400">
                            Type <span className="rounded bg-zinc-100 px-1 py-0.5 font-mono dark:bg-zinc-700">/</span>{" "}
                            for canned responses
                        </p>
                    )}
                </div>
            </div>

            {/* Sidebar */}
            <SidebarPanel
                visible={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                sessionStart={sessionStartTime.current}
                sessionDuration={sessionDuration}
                messageCount={messages.filter((m) => m.type !== "system").length}
            />

            {/* End Chat Overlay */}
            <ChatEndOverlay
                visible={endChatOpen}
                rating={rating}
                onRate={setRating}
                onConfirm={handleEndChat}
                onCancel={() => setEndChatOpen(false)}
            />

            {/* Toast */}
            <Toast message={toastMessage} visible={toastVisible} />
        </div>
    );
}
